/**
 * Generic confirm modal — composes shared Dialog (busy Escape/backdrop,
 * labelledby, alertdialog for danger). OverlayBackDismiss stays local.
 * Always portal to document.body so fixed shell is not clipped by Card
 * `container-type` / CSS grid packing ancestors (G8).
 */
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useOptionalOverlayBackDismiss } from '../context/OverlayBackDismissContext.js';
import { Button } from './Button.js';
import { Dialog } from './Dialog.js';

export interface ConfirmDialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly description: string;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  readonly pending?: boolean;
  /** When true, confirm is not actionable (disabled / omitted from focus). */
  readonly confirmDisabled?: boolean;
  readonly confirmIntent?: 'primary' | 'danger';
  readonly testId?: string;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  pending = false,
  confirmDisabled = false,
  confirmIntent = 'primary',
  testId = 'customer-confirm-dialog',
}: ConfirmDialogProps): JSX.Element | null {
  const overlayBack = useOptionalOverlayBackDismiss();
  const setOverlayDismiss = overlayBack?.setOverlayDismiss;
  const titleId = `${testId}-title`;
  const descriptionId = `${testId}-description`;

  useEffect(() => {
    if (!open || setOverlayDismiss === undefined) {
      return;
    }
    setOverlayDismiss(() => {
      if (!pending) {
        onCancel();
      }
    });
    return (): void => {
      setOverlayDismiss(null);
    };
  }, [open, onCancel, pending, setOverlayDismiss]);

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <Dialog
      open={open}
      onClose={onCancel}
      title={title}
      titleId={titleId}
      describedBy={descriptionId}
      hideHeader
      pending={pending}
      closeOnOverlayClick={false}
      closeLabel={cancelLabel}
      role={confirmIntent === 'danger' ? 'alertdialog' : 'dialog'}
      testId={testId}
      className="max-w-md"
    >
      <h3
        className="text-base font-semibold text-[var(--color-on-surface)]"
        aria-hidden="true"
      >
        {title}
      </h3>
      <p
        id={descriptionId}
        className="mt-2 text-sm text-[var(--color-on-surface-muted)]"
      >
        {description}
      </p>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
        <Button
          intent="secondary"
          type="button"
          disabled={pending}
          onClick={onCancel}
          {...(confirmDisabled ? { 'data-initial-focus': true } : {})}
        >
          {cancelLabel}
        </Button>
        {!confirmDisabled ? (
          <Button
            intent={confirmIntent}
            type="button"
            loading={pending}
            onClick={onConfirm}
            data-initial-focus
            data-testid={`${testId}-confirm`}
          >
            {confirmLabel}
          </Button>
        ) : null}
      </div>
    </Dialog>,
    document.body,
  );
}
